import { useState, useEffect } from 'react';
import { apiGet } from '@/api/client';
import { formatBytes } from '@/util/UtilFunctions';

interface StorageBarProps {
  rootGroupId: string;
  updateModalOpen: (key: string | undefined) => void;
}

interface UsageResponse {
  response: {
    usedBytes?: { N: string };
    sentBytes?: { thisMonth?: { N: string }; lastMonth?: { N: string } };
    storage?: number;
    data_transfer?: number;
  };
}

interface UsageData {
  used: number;
  storage: number;
  sentThisMonth: number;
  dataTransfer: number;
}

function getBarColor(bytes: number, limit: number): string {
  const pct = Math.round((bytes / limit) * 100);
  if (pct < 70) return 'bg-primary';
  if (pct < 100) return 'bg-orange-400';
  return 'bg-destructive';
}

function getBarWidth(bytes: number, limit: number): string {
  const pct = Math.min(Math.round((bytes / limit) * 100), 100);
  return `${pct}%`;
}

export function StorageBar({ rootGroupId, updateModalOpen }: StorageBarProps) {
  const [usage, setUsage] = useState<UsageData | null>(null);

  useEffect(() => {
    let cancelled = false;

    apiGet<UsageResponse>(`/group/usage?gid=${rootGroupId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.response;
        if (typeof data !== 'object' || !data?.storage) return;

        const usedBytes = Number(data.usedBytes?.N) || 0;
        const sentThisMonth = Number(data.sentBytes?.thisMonth?.N) || 0;
        const storage = data.storage * 1_000_000_000;
        const dataTransfer = (data.data_transfer ?? 0) * 1_000_000_000;

        setUsage({ used: usedBytes, storage, sentThisMonth, dataTransfer });

        // Check limits and show warning modals
        if (usedBytes > storage) {
          const accepted = localStorage.getItem('storage-space-limit-notification-accepted');
          if (accepted !== 'true') updateModalOpen('storageLimit');
        }
        if (sentThisMonth > dataTransfer) {
          const accepted = localStorage.getItem('data-transfer-limit-notification-accepted');
          if (accepted !== 'true') updateModalOpen('dataTransfer');
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [rootGroupId, updateModalOpen]);

  if (!usage) return null;

  return (
    <div className="space-y-2.5">
      <div>
        <p className="mb-1 text-xs font-medium text-muted-foreground">Storage</p>
        <div className="h-1.5 w-full rounded-full bg-accent">
          <div
            className={`h-full rounded-full transition-all ${getBarColor(usage.used, usage.storage)}`}
            style={{ width: getBarWidth(usage.used, usage.storage) }}
          />
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {formatBytes(usage.used)} of {formatBytes(usage.storage)} used
        </p>
      </div>
      <div>
        <p className="mb-1 text-xs font-medium text-muted-foreground">Data transfer</p>
        <div className="h-1.5 w-full rounded-full bg-accent">
          <div
            className={`h-full rounded-full transition-all ${getBarColor(usage.sentThisMonth, usage.dataTransfer)}`}
            style={{ width: getBarWidth(usage.sentThisMonth, usage.dataTransfer) }}
          />
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {formatBytes(usage.sentThisMonth)} of {formatBytes(usage.dataTransfer)}{' '}
          used
        </p>
      </div>
    </div>
  );
}
